import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { getTimelineElement } from '../../services/timelineElementSubscriptionService'
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import ReportGmailerrorredRoundedIcon from '@mui/icons-material/ReportGmailerrorredRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import { cardTextureVars, resolveChecklistCardColor } from '../../services/cardBackgroundService'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperWorkspaceSummary } from '../../services/helper/helperStore'
import type { TimelineElement } from '../../types/timelineElement'
import {
  normalizeChecklistBlockedTodoPolicy,
  type ChecklistBlockedTodoPolicy,
} from '../../utils/checklistBlockedTodoPolicy'
import { Button, HiddenFileInput, Pressable } from '../Common'
import { Loader } from '../Common/Loader'

const MotionPressable = motion.create(Pressable)
import { useNls } from '../../nls/nlsProvider'
import { RecurringOccurrenceChip } from '../ChecklistBoard/RecurringOccurrenceChip'
import {
  currentOccurrenceKeyForItem,
  occurrenceKeyFor,
} from '../../services/recurringChecklistService'
import { useWorkspaceDay } from '../../hooks/useWorkspaceDay'
import { buildLockGraph, resolveLock } from '../../services/checklistLockService'
import { useChecklistLockState, type LockTarget } from '../../hooks/useChecklistLockState'
import { HelperReadinessStrip } from './HelperReadinessStrip'
import { HelperEmptyState } from './HelperEmptyState'
import type { HelperReadinessModel } from './helperReadinessModel'
import { formatHelperDueLabel } from './helperSchedule'

const STATUS_TONE: Record<string, string> = {
  ASSIGNED: 'bg-[var(--info-soft)] text-[color-mix(in_srgb,var(--info)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--info)_20%,transparent)]',
  ACCEPTED: 'bg-[color-mix(in_srgb,var(--accent)_15%,transparent)] text-[color-mix(in_srgb,var(--accent)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--accent)_20%,transparent)]',
  ONGOING: 'bg-[color-mix(in_srgb,var(--warning)_15%,transparent)] text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--warning)_20%,transparent)]',
  ASSESSING: 'bg-[color-mix(in_srgb,var(--success)_15%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--success)_20%,transparent)]',
  COMPLETED: 'bg-[color-mix(in_srgb,var(--success)_25%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--success)_30%,transparent)]',
}

const DRAG_COMMIT_RATIO = 0.18
const SWIPE_UP_OPEN_PX = 110

type Props = {
  items: HelperAssignedItem[]
  loading: boolean
  workspaces: HelperWorkspaceSummary[]
  focusItemId?: string | null
  onOpenDetails: (item: HelperAssignedItem) => void
  /** Optional: invoked when the helper takes a quick action on the focused card. */
  onQuickAction?: (
    item: HelperAssignedItem,
    kind: 'PROOF' | 'LOCATION',
    payload: { file?: File; coords?: GeolocationCoordinates },
  ) => void
  blockedTodoPolicy?: ChecklistBlockedTodoPolicy
  /** Currently-selected occurrence pageKey for a recurring item (per-item).
   *  When absent the carousel treats today (DAILY) / this ISO week (WEEKLY)
   *  as the target — same as the historical PROOF quick-action default. */
  getTargetOccurrenceKey?: (item: HelperAssignedItem) => string | null
  /** Called when the helper picks a different target occurrence via the
   *  in-card chip. Parent state drives what the next PROOF upload attaches
   *  to. Non-recurring items never invoke this. */
  onSelectOccurrence?: (item: HelperAssignedItem, pageKey: string) => void
  readinessByItemId?: ReadonlyMap<string, HelperReadinessModel>
  onReportBlocker?: (item: HelperAssignedItem) => void
  onRequestReplenishment?: (item: HelperAssignedItem) => void
  /** Reports the carousel's existing clamped selection without making the
   * parent own a second index state. */
  onActiveItemChange?: (item: HelperAssignedItem | null) => void
  emptyStateTitle?: React.ReactNode
  emptyStateBody?: React.ReactNode
  emptyStateAction?: React.ReactNode
}


type QuickActionState = {
  message: string
  tone: 'success' | 'error' | 'info'
} | null

export const getProofRailState = (item: HelperAssignedItem) => {
  if (!item.requiresProofFile) return null
  if (item.proofFileUrl || item.proofSubmissionId) return { label: 'Proof · done', actionable: false, complete: true }
  return { label: 'Proof required', actionable: true, complete: false }
}

export const getLocationRailState = (item: HelperAssignedItem) => {
  if (!item.requiresLocationPin) return null
  if (item.verifiedCoordinates) return { label: 'Location · done', actionable: false, complete: true }
  return { label: 'Location required', actionable: true, complete: false }
}

export const TodosEmptyState = ({
  title,
  body,
  action,
}: {
  title: React.ReactNode
  body: React.ReactNode
  action?: React.ReactNode
}) => (
  <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="helper-carousel-empty">
    <HelperEmptyState
      icon={<TaskAltRoundedIcon style={{ color: 'var(--accent)' }} />}
      title={title}
      body={body}
      className="flex-none px-0"
    />
    {action}
  </div>
)

const TaskCard = ({
  item,
  workspace,
  parentLookup,
  highlighted,
  onTap,
  dependencyBlocked,
  readiness,
  dragOffsetY,
}: {
  item: HelperAssignedItem
  workspace: HelperWorkspaceSummary | null
  parentLookup: ReadonlyMap<string, Pick<TimelineElement, 'id' | 'parentId' | 'color' | 'type'>>
  highlighted?: boolean
  onTap: () => void
  dependencyBlocked?: boolean
  readiness?: HelperReadinessModel | null
  dragOffsetY: number
}) => {
  const { t } = useNls()
  const status = item.effectiveStatus
  const due = formatHelperDueLabel(item)
  const cardColor = resolveChecklistCardColor(item, parentLookup)
  const cardBackgroundsEnabled = workspace?.cardBackgroundsEnabled !== false
  return (
    <MotionPressable
      onClick={onTap}
      className={`helperTaskCard w-full h-full text-left rounded-[28px] border border-[var(--border-subtle)] shadow-[var(--shadow-card)] p-6 flex flex-col gap-4 relative overflow-hidden bg-[var(--surface-card)] cursor-pointer ${dependencyBlocked ? 'isDependencyBlocked' : ''}`}
      style={cardTextureVars(item, cardColor, { includeImage: cardBackgroundsEnabled })}
      data-testid="helper-task-card"
      data-helper-task-card={item.id}
      animate={highlighted ? {
        boxShadow: [
          'var(--shadow-card)',
          'var(--shadow-card), 0 0 0 2px var(--accent)',
          'var(--shadow-card)',
        ],
      } : undefined}
      transition={{ duration: 1.8, ease: 'easeOut' }}
    >
      <div className="helperTaskCardImage" aria-hidden="true" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {workspace ? (
            <span
              className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded-full bg-[var(--surface-muted)] border border-[var(--border-subtle)] text-[var(--text-strong)] font-medium"
            >
              {workspace.name}
            </span>
          ) : null}
          <span
            className={`px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded-full border font-medium ${
              STATUS_TONE[status] ?? 'bg-[var(--surface-muted)] text-[var(--muted)] border-[var(--border-subtle)]'
            }`}
          >
            {status.toLowerCase()}
          </span>
          {dependencyBlocked ? (
            <span className="px-2.5 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] text-[var(--muted)] font-medium">
              {t('NLSHelperTaskBlocked')}
            </span>
          ) : null}
        </div>
        {due ? (
          <span className="text-[11px] uppercase tracking-widest text-[var(--muted)]">{due}</span>
        ) : null}
      </div>

      <div className="flex-1 flex flex-col gap-4 min-h-0">
        <h3 title={item.title} className="text-[26px] font-semibold text-[var(--text-strong)] leading-tight tracking-tight line-clamp-3">
          {item.title}
        </h3>
        {item.description ? (
          <p className="text-[15px] text-[var(--text)] leading-relaxed line-clamp-6">
            {item.description}
          </p>
        ) : null}

        {item.completionCriteria ? (
          <div className="rounded-2xl bg-[var(--surface-muted)] border border-[var(--border-subtle)] px-3.5 py-3 flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">
              {t('NLSHelperTaskDoneWhen')}
            </span>
            <p className="text-[13px] text-[var(--text-strong)] line-clamp-3">{item.completionCriteria}</p>
          </div>
        ) : null}

        {readiness ? <HelperReadinessStrip readiness={readiness} compact /> : null}
      </div>

      <div className="flex flex-col items-center gap-2 text-[11px] text-[var(--muted)] pt-2 border-t border-[var(--border-subtle)]">
        <div className="flex items-center gap-2">
          {item.requiresProofFile ? (
            <span className="inline-flex items-center gap-1 text-[var(--muted)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--warning)]" />
              {t('NLSHelperTaskProof')}
            </span>
          ) : null}
          {item.requiresLocationPin ? (
            <span className="inline-flex items-center gap-1 text-[var(--muted)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--info)]" />
              {t('NLSHelperTaskLocation')}
            </span>
          ) : null}
        </div>
        <motion.span
          className="text-[var(--muted)] flex flex-col items-center leading-tight"
          animate={{ opacity: dependencyBlocked ? 0.7 : 1 }}
        >
          <ArrowUpwardRoundedIcon style={{ fontSize: '15px' }} />
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={dragOffsetY <= -40 ? 'release' : 'swipe'}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12 }}
            >
              {dragOffsetY <= -40 ? 'Release to open details' : dependencyBlocked ? t('NLSHelperTaskDependencyLocked') : 'Swipe ↑ for details'}
            </motion.span>
          </AnimatePresence>
        </motion.span>
      </div>
    </MotionPressable>
  )
}

type PillIcon = ComponentType<{ style?: React.CSSProperties }>

const Pill = ({
  label,
  Icon,
  disabled,
  onClick,
  testId,
  informational = false,
}: {
  label: string
  Icon: PillIcon
  disabled?: boolean
  onClick: () => void
  testId?: string
  informational?: boolean
}) => (
  <Button
    variant={informational ? 'link' : 'tertiary'}
    size="lg"
    className="flex-1"
    icon={<Icon style={{ fontSize: '18px' }} />}
    onClick={onClick}
    disabled={disabled || informational}
    data-testid={testId}
  >
    {label}
  </Button>
)

export const TaskCarousel = ({
  items,
  loading,
  workspaces,
  focusItemId,
  onOpenDetails,
  onQuickAction,
  blockedTodoPolicy,
  getTargetOccurrenceKey,
  onSelectOccurrence,
  readinessByItemId,
  onReportBlocker,
  onRequestReplenishment,
  onActiveItemChange,
  emptyStateTitle,
  emptyStateBody,
  emptyStateAction,
}: Props) => {
  const { t } = useNls()
  const [index, setIndex] = useState(0)
  const [toast, setToast] = useState<QuickActionState>(null)
  const [dragOffsetY, setDragOffsetY] = useState(0)
  const [parentLookup, setParentLookup] = useState<Map<string, Pick<TimelineElement, 'id' | 'parentId' | 'color' | 'type'>>>(() => new Map())
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const prefersReducedMotion = useReducedMotion()

  const workspaceLookup = useMemo(() => {
    const map = new Map<string, HelperWorkspaceSummary>()
    for (const workspace of workspaces) map.set(workspace.id, workspace)
    return map
  }, [workspaces])

  useEffect(() => {
    if (!focusItemId) return
    const nextIndex = items.findIndex((item) => item.id === focusItemId)
    if (nextIndex >= 0 && nextIndex !== index) setIndex(nextIndex)
  }, [focusItemId, index, items])

  useEffect(() => {
    const activeItem = items.length > 0 ? items[Math.min(index, items.length - 1)] : null
    onActiveItemChange?.(activeItem)
  }, [index, items, onActiveItemChange])

  useEffect(() => {
    let cancelled = false
    const seedIds = [...new Set(items.map((item) => item.parentContainerId).filter(Boolean))]
    if (seedIds.length === 0) {
      setParentLookup(new Map())
      return undefined
    }
    const loadParents = async () => {
      const next = new Map<string, Pick<TimelineElement, 'id' | 'parentId' | 'color' | 'type'>>()
      const queue = [...seedIds]
      const seen = new Set<string>()
      while (queue.length > 0 && seen.size < 240) {
        const elementId = queue.shift()
        if (!elementId || seen.has(elementId)) continue
        seen.add(elementId)
        const element = await getTimelineElement(elementId)
        if (!element) continue
        const parent = {
          id: element.id,
          parentId: typeof element.parentId === 'string' ? element.parentId : null,
          color: element.color,
          type: element.type ?? 'TASK',
        } satisfies Pick<TimelineElement, 'id' | 'parentId' | 'color' | 'type'>
        next.set(parent.id, parent)
        if (parent.parentId) queue.push(parent.parentId)
      }
      if (!cancelled) setParentLookup(next)
    }
    void loadParents().catch(() => {
      if (!cancelled) setParentLookup(new Map())
    })
    return () => { cancelled = true }
  }, [items])

  const clampedIndex = items.length > 0 ? Math.min(index, items.length - 1) : 0
  const active = items.length > 0 ? items[clampedIndex] : null
  const { timeZone, todayIso, weekKey } = useWorkspaceDay(active?.workspaceId)
  const targetKey = active ? (getTargetOccurrenceKey?.(active) ?? (active.checklistType === 'WEEKLY' ? weekKey : todayIso)) : null
  const activeId = active?.id
  const carouselTargets = useMemo<LockTarget[]>(() => {
    if (!activeId || !targetKey) return []
    return [{ itemId: activeId, periodKey: targetKey }]
  }, [activeId, targetKey])

  const lockMap = useChecklistLockState(items, carouselTargets, {
    workspaceId: active?.workspaceId,
    timeZone,
  })

  const lock = useMemo(() => {
    if (!active || !targetKey) return null
    return lockMap.get(`${active.id}:${targetKey}`) ?? lockMap.get(`${active.id}|${targetKey}`) ?? resolveLock(active, targetKey, buildLockGraph(items), lockMap.pages, undefined, timeZone)
  }, [active, items, lockMap, targetKey, timeZone])

  if (loading) {
    return (
      <div className="flex-1 grid place-items-center text-sm text-[var(--muted)] appInlineLoadingState" data-testid="helper-carousel-loading">
        <Loader size={150} label={t('NLSHelperTaskLoading')} />
      </div>
    )
  }

  if (items.length === 0 || !active) {
    return <TodosEmptyState title={emptyStateTitle ?? t('NLSHelperTaskEmptyTitle')} body={emptyStateBody ?? t('NLSHelperTaskEmptyBody')} action={emptyStateAction} />
  }

  const next = items[clampedIndex + 1] ?? null
  const prev = items[clampedIndex - 1] ?? null
  const workspacePolicy = workspaceLookup.get(active.workspaceId)?.checklistBlockedTodoPolicy
  const policy = blockedTodoPolicy ?? normalizeChecklistBlockedTodoPolicy(
    workspacePolicy ? { checklistBlockedTodoPolicy: workspacePolicy } : null,
  )
  const dependencyBlocked = lock ? lock.isLocked : (active.dependencyBlocked === true)
  const proofDisabledByDependency = dependencyBlocked && !policy.allowProof
  const activeReadiness = readinessByItemId?.get(active.id) ?? null
  const proofRail = getProofRailState(active)
  const locationRail = getLocationRailState(active)

  const handleDragEnd = (offsetX: number, offsetY: number) => {
    if (offsetY < -SWIPE_UP_OPEN_PX) {
      onOpenDetails(active)
      return
    }
    const threshold = Math.max(window.innerWidth * DRAG_COMMIT_RATIO, 60)
    if (offsetX < -threshold && clampedIndex < items.length - 1) setIndex(clampedIndex + 1)
    else if (offsetX > threshold && clampedIndex > 0) setIndex(clampedIndex - 1)
  }

  const flash = (message: string, tone: QuickActionState extends infer T ? T extends { tone: infer U } ? U : never : never) => {
    setToast({ message, tone })
    window.setTimeout(() => setToast(null), 2200)
  }

  const handleUploadProof = () => {
    if (proofDisabledByDependency) {
      flash(t('NLSHelperTaskProofUploadBlocked'), 'error')
      return
    }
    if (!active.requiresProofFile) return
    fileInputRef.current?.click()
  }

  const handleProofFilesSelected = (files: FileList) => {
    const file = files[0]
    if (!file) return
    try {
      onQuickAction?.(active, 'PROOF', { file })
      flash(t('NLSHelperTaskProofAttached', { name: file.name }), 'success')
    } catch (error) {
      flash(error instanceof Error ? error.message : t('NLSHelperTaskProofAttachFailed'), 'error')
    }
  }

  const handleLocationCheckIn = () => {
    if (!active.requiresLocationPin) return
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      flash(t('NLSHelperTaskLocationUnavailable'), 'error')
      return
    }
    flash(t('NLSHelperTaskLocating'), 'info')
    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          onQuickAction?.(active, 'LOCATION', { coords: position.coords })
          flash(
            t('NLSHelperTaskLocationPinned', {
              lat: position.coords.latitude.toFixed(4),
              lng: position.coords.longitude.toFixed(4),
            }),
            'success',
          )
        } catch (error) {
          flash(error instanceof Error ? error.message : t('NLSHelperTaskLocationRecordFailed'), 'error')
        }
      },
      (error) => flash(error.message || t('NLSHelperTaskLocationPermissionDenied'), 'error'),
      { enableHighAccuracy: true, timeout: 10_000 },
    )
  }

  return (
    <div className="relative flex-1 flex flex-col gap-4" data-testid="helper-carousel">
      <HiddenFileInput
        ref={fileInputRef}
        accept="image/*,application/pdf"
        onFiles={handleProofFilesSelected}
        testId="helper-task-proof-input"
      />

      <div className="relative flex-1 min-h-[420px]">
        {prev ? (
          <div
            className="absolute inset-y-6 -left-3 w-6 rounded-l-3xl border-l border-y border-[var(--border-subtle)] bg-[var(--surface-muted)] opacity-60"
            aria-hidden="true"
          />
        ) : null}
        {next ? (
          <div
            className="absolute inset-y-6 -right-3 w-6 rounded-r-3xl border-r border-y border-[var(--border-subtle)] bg-[var(--surface-muted)] opacity-60"
            aria-hidden="true"
          />
        ) : null}

        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={active.id}
            className="absolute inset-0 px-1"
            drag={prefersReducedMotion ? false : true}
            dragElastic={0.24}
            dragMomentum
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            onDrag={(_, info) => setDragOffsetY(info.offset.y)}
            onDragEnd={(_, info) => {
              setDragOffsetY(0)
              handleDragEnd(info.offset.x, info.offset.y)
            }}
            initial={{ opacity: 0, scale: 0.92, y: 18, rotateZ: -1.8 }}
            animate={{ opacity: 1, scale: 1, y: 0, rotateZ: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -14, rotateZ: 1.4 }}
            transition={prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 220, damping: 26, mass: 0.9 }}
          >
            <TaskCard
              item={active}
              workspace={workspaceLookup.get(active.workspaceId) ?? null}
              parentLookup={parentLookup}
              highlighted={focusItemId === active.id}
              onTap={() => onOpenDetails(active)}
              dependencyBlocked={dependencyBlocked}
              readiness={activeReadiness}
              dragOffsetY={dragOffsetY}
            />
          </motion.div>
        </AnimatePresence>
      </div>

      {items.length > 1 && <div className="flex items-center justify-center gap-1.5" data-testid="helper-carousel-dots">
        {items.map((entry, dotIndex) => {
          const isActive = dotIndex === clampedIndex
          return (
            <Pressable
              key={entry.id}
              onClick={() => setIndex(dotIndex)}
              aria-label={t('NLSHelperTaskGoToTask', { count: dotIndex + 1 })}
              aria-pressed={isActive}
              className={`h-1.5 rounded-full transition-all ${
                isActive ? 'w-6 bg-[var(--accent)]' : 'w-1.5 bg-[var(--border-subtle)] hover:bg-[var(--border-strong)]'
              }`}
            />
          )
        })}
      </div>}

      <div className="flex items-center gap-2.5 flex-wrap" data-testid="helper-quick-actions">
        {proofRail ? (
          <Pill
            label={proofDisabledByDependency ? t('NLSHelperTaskProofLocked') : proofRail.label}
            Icon={proofRail.complete ? CheckRoundedIcon : proofRail.actionable ? AddRoundedIcon : AttachFileRoundedIcon}
            disabled={proofDisabledByDependency}
            informational={!proofRail.actionable && !proofDisabledByDependency}
            onClick={handleUploadProof}
            testId="helper-quick-action-proof"
          />
        ) : null}
        {locationRail ? (
          <Pill
            label={locationRail.label}
            Icon={locationRail.complete ? CheckRoundedIcon : locationRail.actionable ? AddRoundedIcon : LocationOnRoundedIcon}
            informational={!locationRail.actionable}
            onClick={handleLocationCheckIn}
            testId="helper-quick-action-location"
          />
        ) : null}
        {activeReadiness?.needsStock && onRequestReplenishment ? (
          <Pill
            label={t('NLSHelperReadinessNeedMore')}
            Icon={Inventory2RoundedIcon}
            onClick={() => onRequestReplenishment(active)}
            testId="helper-quick-action-need-more"
          />
        ) : null}
        {onReportBlocker ? (
          <Pill
            label={t('NLSHelperBlockerReportTitle')}
            Icon={ReportGmailerrorredRoundedIcon}
            onClick={() => onReportBlocker(active)}
            testId="helper-quick-action-report-blocker"
          />
        ) : null}
        {(active.checklistType === 'DAILY' || active.checklistType === 'WEEKLY') ? (
          <RecurringOccurrenceChip
            item={active}
            mode={active.checklistType}
            focusedKey={
              getTargetOccurrenceKey?.(active) ?? currentOccurrenceKeyForItem(active, new Date(), timeZone) ?? undefined
            }
            onJump={(date) => {
              if (!onSelectOccurrence) return
              const pageKey = occurrenceKeyFor(active.checklistType as 'DAILY' | 'WEEKLY', date, timeZone)
              onSelectOccurrence(active, pageKey)
            }}
          />
        ) : null}
      </div>

      {toast ? (
        <div
          className={`absolute left-1/2 -translate-x-1/2 -top-2 px-3 py-1.5 rounded-full text-[11px] font-medium border shadow-[var(--shadow-pop)]
            ${toast.tone === 'success' ? 'bg-[color-mix(in_srgb,var(--success)_15%,transparent)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--success)_30%,transparent)]' : ''}
            ${toast.tone === 'error' ? 'bg-[color-mix(in_srgb,var(--danger)_15%,transparent)] text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))] border-[color-mix(in_srgb,var(--danger)_30%,transparent)]' : ''}
            ${toast.tone === 'info' ? 'bg-[var(--surface-muted)] text-[var(--muted)] border-[var(--border-subtle)]' : ''}
          `}
          role="status"
        >
          {toast.message}
        </div>
      ) : null}
    </div>
  )
}
